/**
 * The dialog a readiness gate is overridden through, and the reason it
 * demands before it will let one past.
 *
 * @module components/governance/ReadinessOverrideDialog
 */

import React from "react";

import type { WorkflowQueueAction } from "@/components/shared/workflow-queue-actions";
import { Alert, Button, Checkbox, Heading, KeyValue, Modal, Text } from "@/components/ui";
import { FormField, TextAreaField } from "@/components/ui/Form";
import type { EditorProgrammeMetadata } from "@/platform";
import type { ReadinessBlocker, ReadinessState } from "@/server/types";

import { PROGRAMME_SECTION_LABELS } from "./governance-stations";

export interface ReadinessOverrideDialogProps {
  overrideAction: WorkflowQueueAction | undefined;
  overrideGate: ReadinessState;
  overrideBlockers: ReadinessBlocker[];
  overrideReason: string;
  onOverrideReasonChange: (value: string) => void;
  overrideConfirmed: boolean;
  onOverrideConfirmedChange: (value: boolean) => void;
  actionPending: boolean;
  error: Error | null;
  programme: EditorProgrammeMetadata;
  onApply: () => void;
  onClose: () => void;
}

/** What the submit is still waiting for, or nothing once it may go. */
function submitRefusal(reason: string, confirmed: boolean): string | undefined {
  if (!reason.trim()) {
    return "Give a reason for the override";
  }
  return confirmed ? undefined : "Confirm the requirements remain unresolved";
}

export function ReadinessOverrideDialog({
  overrideAction,
  overrideGate,
  overrideBlockers,
  overrideReason,
  onOverrideReasonChange,
  overrideConfirmed,
  onOverrideConfirmedChange,
  actionPending,
  error,
  programme,
  onApply,
  onClose,
}: ReadinessOverrideDialogProps): React.ReactElement | null {
  if (!overrideAction || !overrideAction.nextState) {
    return null;
  }
  const refusal = submitRefusal(overrideReason, overrideConfirmed);
  return (
    <Modal
      open
      tone="danger"
      size="lg"
      title="Override readiness blockers?"
      subtitle={`This permits one transition to ${overrideAction.nextState}. It does not resolve the requirements, and the exception is kept with the approval evidence.`}
      onClose={onClose}
      closeRefusal={actionPending ? "The override is being recorded" : undefined}
      data-testid="readiness-override-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            disabled={actionPending}
            disabledReason="Wait for the action in progress to finish."
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="danger"
            icon="arrow-right"
            loading={actionPending}
            loadingLabel="Recording the override"
            disabled={Boolean(refusal)}
            disabledReason={refusal}
            onClick={onApply}
            data-testid="readiness-override-submit"
          >
            Record override and continue
          </Button>
        </>
      }
    >
      <KeyValue
        entries={[
          { label: "Current revision", value: programme.saveRevision },
          { label: "Required gate", value: overrideGate },
          { label: "Target state", value: overrideAction.nextState },
        ]}
      />

      <div className="mt-4" data-testid="readiness-override-blockers">
        <Heading level={3} size="md">
          Bypassed requirements ({overrideBlockers.length})
        </Heading>
        <ul className="mt-2 max-h-52 space-y-2 overflow-y-auto">
          {overrideBlockers.map((blocker) => (
            <li
              key={`${blocker.requirement}-${blocker.target?.section ?? "programme"}-${blocker.message}`}
            >
              <Alert tone="warning" role="none">
                <span>
                  {blocker.message}
                  <Text as="span" size="sm" tone="muted" className="mt-1 block">
                    {blocker.target?.section
                      ? (PROGRAMME_SECTION_LABELS[blocker.target.section] ?? blocker.target.section)
                      : "Programme-wide requirement"}
                  </Text>
                </span>
              </Alert>
            </li>
          ))}
        </ul>
      </div>

      <FormField
        label="Reason for override"
        htmlFor="readiness-override-reason"
        className="mt-4"
        helpText="Explain why governance may proceed while these requirements remain unresolved."
      >
        <TextAreaField
          id="readiness-override-reason"
          value={overrideReason}
          onChange={onOverrideReasonChange}
          rows={4}
          className="w-full"
          data-testid="readiness-override-reason"
        />
      </FormField>

      <div className="mt-4">
        <Checkbox
          id="readiness-override-confirmation"
          checked={overrideConfirmed}
          onChange={onOverrideConfirmedChange}
          label="I confirm these requirements remain unresolved and this decision will be retained in the programme audit and approval record."
          data-testid="readiness-override-confirmation"
        />
      </div>

      {/* The dialog covers the panel's own alert, so a refused override has
          nowhere else to report itself. */}
      {error ? (
        <Alert tone="warning" className="mt-4" data-testid="readiness-override-error">
          {error.message}
        </Alert>
      ) : null}
    </Modal>
  );
}
